// src/components/admin/AdminContact.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    FaEnvelope,
    FaPhone,
    FaClock,
    FaShareAlt,
    FaLink,
    FaSave,
    FaCheck,
    FaSpinner,
} from "react-icons/fa";
import { CONTACT_KEYS, saveContactSettings } from "../../services/adminContactService";
import { getAllSettings, toValueMap } from "../../services/adminSettingsService";
import { AdminErrorBanner } from "../common/AdminBanner";
import "./AdminContact.css";

interface AdminContactProps {
    isAdmin: boolean;
}

// ── Field definitions ──────────────────────────────────────────────────────────

interface FieldDef {
    key: string;
    labelKey: string;
    type?: "text" | "textarea";
    placeholder?: string;
}

interface GroupDef {
    id: string;
    icon: React.ReactNode;
    titleKey: string;
    fields: FieldDef[];
    // Optional explanatory line rendered above the fields. Used by the legal
    // group, whose fields became optional once the platform started publishing
    // its own legal pages.
    noteKey?: string;
    noteFallback?: string;
}

const GROUPS: GroupDef[] = [
    {
        id: "emails",
        icon: <FaEnvelope />,
        titleKey: "admin_contact_group_emails",
        fields: [
            { key: CONTACT_KEYS.EMAIL_GENERAL,    labelKey: "admin_contact_email_general",    placeholder: "iletisim@example.com" },
            { key: CONTACT_KEYS.EMAIL_ADVERTISING, labelKey: "admin_contact_email_advertising", placeholder: "reklam@example.com" },
            { key: CONTACT_KEYS.EMAIL_COMPLAINT,   labelKey: "admin_contact_email_complaint",   placeholder: "sikayet@example.com" },
            { key: CONTACT_KEYS.EMAIL_PRESS,       labelKey: "admin_contact_email_press",       placeholder: "basin@example.com" },
            { key: CONTACT_KEYS.EMAIL_DEVELOPER,   labelKey: "admin_contact_email_developer",   placeholder: "gelistirici@example.com" },
        ],
    },
    {
        id: "address",
        icon: <FaPhone />,
        titleKey: "admin_contact_group_address",
        fields: [
            { key: CONTACT_KEYS.PHONE,   labelKey: "admin_contact_phone",   placeholder: "+90 212 000 00 00" },
            { key: CONTACT_KEYS.ADDRESS, labelKey: "admin_contact_address", type: "textarea", placeholder: "Adres satırı 1\nAdres satırı 2" },
        ],
    },
    {
        id: "info",
        icon: <FaClock />,
        titleKey: "admin_contact_group_info",
        fields: [
            { key: CONTACT_KEYS.WORKING_HOURS, labelKey: "admin_contact_working_hours", placeholder: "Hafta içi 09:00 – 18:00" },
            { key: CONTACT_KEYS.RESPONSE_TIME, labelKey: "admin_contact_response_time", placeholder: "24 saat içinde dönüş yapılır" },
        ],
    },
    {
        id: "social",
        icon: <FaShareAlt />,
        titleKey: "admin_contact_group_social",
        fields: [
            { key: CONTACT_KEYS.SOCIAL_X,        labelKey: "admin_contact_social_x",        placeholder: "https://x.com/hesap" },
            { key: CONTACT_KEYS.SOCIAL_INSTAGRAM, labelKey: "admin_contact_social_instagram", placeholder: "https://instagram.com/hesap" },
            { key: CONTACT_KEYS.SOCIAL_YOUTUBE,   labelKey: "admin_contact_social_youtube",   placeholder: "https://youtube.com/@kanal" },
            { key: CONTACT_KEYS.SOCIAL_TIKTOK,    labelKey: "admin_contact_social_tiktok",    placeholder: "https://tiktok.com/@hesap" },
        ],
    },
    {
        id: "legal",
        icon: <FaLink />,
        titleKey: "admin_contact_group_legal",
        noteKey: "admin_contact_legal_note",
        noteFallback: "Yasal metinler artık \"Yasal Metinler\" sekmesinden düzenleniyor ve /kullanim-kosullari, /gizlilik-politikasi, /cerez-politikasi adreslerinde yayımlanıyor. Aşağıdaki alanları yalnızca metinleri başka bir adreste barındırıyorsanız doldurun; boş bırakırsanız footer yerleşik sayfalara bağlanır.",
        fields: [
            { key: CONTACT_KEYS.URL_PRIVACY, labelKey: "admin_contact_url_privacy", placeholder: "/gizlilik-politikasi" },
            { key: CONTACT_KEYS.URL_TERMS,   labelKey: "admin_contact_url_terms",   placeholder: "/kullanim-kosullari" },
        ],
    },
];

// ── Component ──────────────────────────────────────────────────────────────────

export const AdminContact: React.FC<AdminContactProps> = ({ isAdmin }) => {
    const { t } = useTranslation();

    const [values, setValues]   = useState<Record<string, string>>({});
    const [saving, setSaving]   = useState<string | null>(null);  // group id being saved
    const [saved,  setSaved]    = useState<string | null>(null);  // group id recently saved
    const [error,  setError]    = useState<string | null>(null);

    // Load all system settings on mount and extract contact keys
    const loadSettings = useCallback(async () => {
        try {
            const all = await getAllSettings();
            const map = toValueMap(all);
            const contactValues: Record<string, string> = {};
            Object.values(CONTACT_KEYS).forEach((key) => {
                contactValues[key] = map[key] ?? "";
            });
            setValues(contactValues);
        } catch {
            setError(t("admin_contact_load_error", "İletişim bilgileri yüklenemedi."));
        }
    }, [t]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleChange = (key: string, value: string) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async (group: GroupDef) => {
        if (!isAdmin) return;
        setError(null);
        setSaving(group.id);
        try {
            const payload: Record<string, string> = {};
            group.fields.forEach((f) => {
                payload[f.key] = values[f.key] ?? "";
            });
            await saveContactSettings(payload);
            setSaved(group.id);
            setTimeout(() => setSaved(null), 2500);
        } catch {
            setError(t("admin_contact_save_error", "Kaydetme başarısız oldu."));
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="admin-contact">
            {/* AdminErrorBanner only accepts message prop — no onClose */}
            <AdminErrorBanner message={error} />

            {GROUPS.map((group) => (
                <div key={group.id} className="contact-group">
                    {/* Group header */}
                    <div className="contact-group-header">
                        <span className="contact-group-icon">{group.icon}</span>
                        <span className="contact-group-title">{t(group.titleKey)}</span>
                    </div>

                    {/* Fields */}
                    <div className="contact-group-body">
                        {group.noteKey && (
                            <p className="contact-group-note">
                                {t(group.noteKey, group.noteFallback ?? "")}
                            </p>
                        )}
                        {group.fields.map((field) => (
                            <div key={field.key} className="contact-field-row">
                                <label className="contact-field-label">
                                    {t(field.labelKey)}
                                </label>
                                {field.type === "textarea" ? (
                                    <textarea
                                        className="contact-field-input contact-field-textarea"
                                        value={values[field.key] ?? ""}
                                        onChange={(e) => handleChange(field.key, e.target.value)}
                                        placeholder={field.placeholder}
                                        rows={3}
                                        disabled={!isAdmin}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        className="contact-field-input"
                                        value={values[field.key] ?? ""}
                                        onChange={(e) => handleChange(field.key, e.target.value)}
                                        placeholder={field.placeholder}
                                        disabled={!isAdmin}
                                    />
                                )}
                            </div>
                        ))}

                        {/* Save button — admin only */}
                        {isAdmin && (
                            <div className="contact-group-footer">
                                <button
                                    className="contact-save-btn"
                                    onClick={() => handleSave(group)}
                                    disabled={saving === group.id}
                                >
                                    {saving === group.id ? (
                                        <>
                                            <FaSpinner className="contact-save-spinner" />
                                            {t("admin_settings_saving", "Kaydediliyor...")}
                                        </>
                                    ) : saved === group.id ? (
                                        <>
                                            <FaCheck />
                                            {t("admin_settings_saved", "Kaydedildi")}
                                        </>
                                    ) : (
                                        <>
                                            <FaSave />
                                            {t("admin_settings_save", "Kaydet")}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};